/**
 * SettingsContext
 * Fetches store settings from the API once on app load and provides them
 * to every component. When admin saves settings → call refresh() → all
 * components update instantly without a page reload.
 */
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";

const SettingsContext = createContext(null);

const DEFAULTS = {
  store_name:              "BCP Brisbane Car Parts",
  store_phone:             "0406 291 986",
  store_phone_2:           "1800 650 650",
  store_email:             "sales@bcpcarparts.com.au",
  store_address:           "Brisbane, QLD",
  abn:                     "",
  business_hours:          "Mon–Fri 8:00–5:30 · Sat 9:00–1:00",
  logo_url:                "/logo.png",
  cod_enabled:             true,
  stripe_enabled:          true,
  tax_rate:                0.10,
  shipping_flat:           15.0,
  free_shipping_threshold: 250.0,
  allow_backorder:         false,
  return_policy:           "",
  shipping_policy:         "",
  warranty_policy:         "",
};

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded]     = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings({
        ...DEFAULTS,
        ...data,
        // Always have a logo — fall back to the file in /public
        logo_url: data.logo_url || "/logo.png",
      });
    } catch {
      // Network error — use defaults, system still works
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <SettingsContext.Provider value={{ settings, refresh, loaded }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used inside <SettingsProvider>");
  return ctx;
};